'use client';

import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import WapOnly from '@/components/Responsive/WapOnly';
import WebOnly from '@/components/Responsive/WebOnly';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  Box,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Popover,
  Tab,
  Tabs,
  TextField,
} from '@mui/material';
import { CheckOutlined, CollapseArrowDownOutlined, RightArrowOutlined } from '@zeelool/icon';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Control, Controller, RegisterOptions } from 'react-hook-form';

// 级联选择器选项接口
export interface CascaderOption {
  value: string | number;
  label: string;
  children?: CascaderOption[];
  disabled?: boolean;
  [key: string]: any;
}

// 级联选择器配置
export interface CascaderProps {
  value?: Array<string | number>;
  defaultValue?: Array<string | number>;
  options?: CascaderOption[];
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  separator?: string;
  showAllLevels?: boolean;
  props?: {
    value?: string;
    label?: string;
    children?: string;
    disabled?: string;
  };
  onChange?: (value: Array<string | number>, selectedOptions?: CascaderOption[]) => void;

  // react-hook-form 支持
  name?: string;
  control?: Control<any>;
  rules?: RegisterOptions;

  // 样式相关
  label?: string | React.ReactNode;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  fullWidth?: boolean;
  size?: 'small' | 'medium';
  variant?: 'outlined' | 'filled' | 'standard';

  // 表单项样式
  formItemClassName?: string;
  layout?: 'inline' | 'vertical';
}

const Cascader: React.FC<CascaderProps> = ({
  value,
  defaultValue = [],
  options = [],
  placeholder = 'Please select',
  disabled = false,
  clearable = true,
  separator = ' / ',
  showAllLevels = true,
  props: fieldProps = {},
  onChange,
  name,
  control,
  rules,
  label,
  required = false,
  error = false,
  helperText,
  fullWidth = true,
  size = 'medium',
  variant = 'outlined',
  formItemClassName = 'mb-[24px]',
  layout = 'inline',
}) => {
  // Web
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  // Wap
  const [mobileOpen, setMobileOpen] = useState(false);
  const [pendingOption, setPendingOption] = useState<CascaderOption>(null); // 当前选择但未提交的项
  const [currentLevel, setCurrentLevel] = useState(0); // 移动端当前层级

  // 共用
  // 选择的路径名
  const [selectedPath, setSelectedPath] = useState<Array<string | number>>(
    value || defaultValue || [],
  );
  // 选项面板
  const [panels, setPanels] = useState<CascaderOption[][]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // 获取设备类型
  const { deviceType } = useContext(ResponsiveContext);
  const isMobile = deviceType !== EPlatform.WEB;

  // 字段配置默认值
  const {
    value: valueKey = 'value',
    label: labelKey = 'label',
    children: childrenKey = 'children',
    disabled: disabledKey = 'disabled',
  } = fieldProps;

  // 构建级联面板数据
  const buildPanels = useCallback(
    (path: Array<string | number>) => {
      const newPanels: CascaderOption[][] = [options];
      let currentOptions = options;

      for (const _value of path) {
        const option = currentOptions.find((opt) => opt[valueKey] === _value);
        if (option && option[childrenKey]) {
          currentOptions = option[childrenKey];
          newPanels.push(currentOptions);
        } else {
          break;
        }
      }

      return newPanels;
    },
    [options, valueKey, childrenKey],
  );

  // 更新面板显示
  useEffect(() => {
    const newPanels = buildPanels(selectedPath);
    setPanels(newPanels);

    // 移动端设置当前层级
    // if (isMobile) {
    //   setCurrentLevel(Math.max(0, selectedPath.length));
    // }
  }, [selectedPath, buildPanels, isMobile]);

  // 是否有子节点
  const getOptionHasLeafNode = (option: CascaderOption, returnedIfNonOption = true) => {
    if (!option) return returnedIfNonOption;
    return option[childrenKey] && option[childrenKey].length > 0;
  };

  // 获取选中项的显示文本
  const getDisplayText = useCallback(
    (path: Array<string | number>): string => {
      if (path.length === 0) return '';

      const labels: string[] = [];
      let currentOptions = options;

      for (let i = 0; i < path.length; i++) {
        const _value = path[i];
        const option = currentOptions.find((opt) => opt[valueKey] === _value);
        if (option) {
          labels.push(option[labelKey]);
          if (option[childrenKey]) {
            // 最后一项但是还有子节点，不显示
            if (i === path.length - 1) return '';
            currentOptions = option[childrenKey];
          }
        }
      }

      return showAllLevels ? labels.join(separator) : labels[labels.length - 1] || '';
    },
    [options, valueKey, labelKey, childrenKey, separator, showAllLevels],
  );

  // 处理选项点击 - 桌面端
  const handleOptionClick = useCallback(
    (option: CascaderOption, level: number) => {
      const newPath = [...selectedPath.slice(0, level), option[valueKey]];

      // 如果有子项，继续展开
      if (option[childrenKey] && option[childrenKey].length > 0) {
        setSelectedPath(newPath);
      } else {
        // 叶子节点，完成选择
        setSelectedPath(newPath);
        setAnchorEl(null);

        // 触发change事件
        if (onChange) {
          onChange(newPath, [option]);
        }
      }
    },
    [selectedPath, valueKey, childrenKey, onChange],
  );

  // 处理选项点击 - 移动端
  const handleMobileOptionClick = useCallback(
    (option: CascaderOption, level: number) => {
      const newPath = [...selectedPath.slice(0, level), option[valueKey]];

      // 如果有子项，进入下一层
      if (option[childrenKey] && option[childrenKey].length > 0) {
        setSelectedPath(newPath);
        setCurrentLevel(level + 1);
      } else {
        // 叶子节点，完成选择
        setSelectedPath(newPath);
        setMobileOpen(false);
        setCurrentLevel(level);

        // 触发change事件
        if (onChange) {
          onChange(newPath, [option]);
        }
      }
    },
    [selectedPath, valueKey, childrenKey, onChange],
  );

  // 处理输入框点击
  const handleInputClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      if (disabled) return;

      if (isMobile) {
        setMobileOpen(true);
        // setCurrentLevel(Math.max(0, selectedPath.length));
      } else {
        setAnchorEl(event.currentTarget);
      }
    },
    [disabled, isMobile, selectedPath.length],
  );

  // 关闭弹窗
  const handleClose = useCallback(() => {
    setAnchorEl(null);
    setMobileOpen(false);
    // setCurrentLevel(0);
  }, []);

  // 清空选择
  const handleClear = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      setSelectedPath([]);
      if (onChange) {
        onChange([], []);
      }
    },
    [onChange],
  );

  // 移动端返回上一级
  const handleMobileBack = useCallback(() => {
    if (currentLevel > 0) {
      setCurrentLevel(currentLevel - 1);
      // 更新选中路径到当前级别
      setSelectedPath(selectedPath.slice(0, currentLevel - 1));
    }
  }, [currentLevel, selectedPath]);

  // 获取移动端当前标题
  const getMobileTitle = useCallback(() => {
    if (currentLevel === 0) {
      return label || 'Please select';
    }

    // 获取当前级别的标题
    const pathLabels: string[] = [];
    let currentOptions = options;

    for (let i = 0; i < currentLevel && i < selectedPath.length; i++) {
      const _value = selectedPath[i];
      const option = currentOptions.find((opt) => opt[valueKey] === _value);
      if (option) {
        pathLabels.push(option[labelKey]);
        if (option[childrenKey]) {
          currentOptions = option[childrenKey];
        }
      }
    }

    return pathLabels.join(' / ') || 'Select';
  }, [currentLevel, label, options, selectedPath, valueKey, labelKey, childrenKey]);

  // 渲染桌面端级联面板
  const renderDesktopPanel = (panelOptions: CascaderOption[], level: number) => {
    return (
      <Paper
        key={level}
        sx={{
          minWidth: 'fit-content',
          maxWidth: 300,
          maxHeight: 300,
          overflow: 'auto',
          borderRight: level < panels.length - 1 ? '1px solid #e0e0e0' : 'none',
        }}
      >
        <List dense>
          {panelOptions.map((option) => {
            const isSelected = selectedPath[level] === option[valueKey];
            const isDisabled = option[disabledKey];
            const hasChildren = option[childrenKey] && option[childrenKey].length > 0;

            return (
              <ListItem key={option[valueKey]} disablePadding>
                <ListItemButton
                  selected={isSelected}
                  disabled={isDisabled}
                  onClick={() => !isDisabled && handleOptionClick(option, level)}
                  sx={{
                    '&.Mui-selected': {
                      backgroundColor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                      },
                    },
                  }}
                >
                  <ListItemText primary={option[labelKey]} />
                  {hasChildren && <RightArrowOutlined sx={{ fontSize: 16, ml: 1 }} />}
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Paper>
    );
  };

  // 渲染移动端选项列表
  const renderMobileOptions = () => {
    const currentOptions = panels[currentLevel] || [];

    return (
      <div className="mobile-cascader-options">
        {currentOptions.map((option) => {
          const isSelected = selectedPath[currentLevel] === option[valueKey];
          const isDisabled = option[disabledKey];
          const hasChildren = option[childrenKey] && option[childrenKey].length > 0;

          return (
            <div
              key={option[valueKey]}
              className={`
                flex items-center justify-between h-[48px] px-4 border-b border-gray-100 cursor-pointer
                ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50'}
                ${isSelected ? 'bg-blue-50 text-primary-purple' : ''}
              `}
              onClick={() => !isDisabled && handleMobileOptionClick(option, currentLevel)}
            >
              <span className="text-base">{option[labelKey]}</span>
              {hasChildren && <RightArrowOutlined className="text-gray-400 text-sm" />}
            </div>
          );
        })}
      </div>
    );
  };

  // 渲染移动端自定义标题
  const renderMobileTitle = () => {
    return (
      <div className="flex items-center justify-between h-[56px] px-4 bg-white border-b border-gray-100">
        {currentLevel > 0 && (
          <button
            onClick={handleMobileBack}
            className="flex items-center text-primary-purple text-sm"
          >
            <span className="mr-1">‹</span>
            Back
          </button>
        )}
        <span className="text-base font-medium flex-1 text-center">{getMobileTitle()}</span>
        <button
          onClick={handleClose}
          className="text-gray-400 text-lg w-24 h-24 flex items-center justify-center"
        >
          ×
        </button>
      </div>
    );
  };

  // 如果没有control，直接返回基本组件
  if (!control || !name) {
    const open = Boolean(anchorEl);
    const displayText = getDisplayText(selectedPath);

    return (
      <div className={`relative ${formItemClassName}`}>
        {layout === 'vertical' && label && (
          <label className="block text-[16px] text-primary-text font-br mb-[12px]">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <TextField
          ref={inputRef}
          fullWidth={fullWidth}
          size={size}
          variant={variant}
          label={layout === 'vertical' ? undefined : label}
          placeholder={placeholder}
          value={displayText}
          error={error}
          helperText={helperText}
          disabled={disabled}
          required={required}
          onClick={handleInputClick}
          slotProps={{
            input: {
              readOnly: true,
            },
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {clearable && selectedPath.length > 0 && !disabled && (
                  <span
                    onClick={handleClear}
                    style={{
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginRight: '8px',
                      color: '#999',
                    }}
                  >
                    ✕
                  </span>
                )}
                <CollapseArrowDownOutlined
                  sx={{
                    transform: open || mobileOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.2s',
                    cursor: 'pointer',
                  }}
                />
              </InputAdornment>
            ),
          }}
        />

        {/* 桌面端 Popover */}
        <WebOnly>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            PaperProps={{
              sx: {
                mt: 1,
                maxHeight: 350,
                minWidth: anchorEl?.clientWidth || 200,
              },
            }}
          >
            <Box sx={{ display: 'flex' }}>
              {panels.map((panelOptions, level) => renderDesktopPanel(panelOptions, level))}
            </Box>
          </Popover>
        </WebOnly>

        {/* 移动端 Popup */}
        <WapOnly>
          <Popup
            open={mobileOpen}
            onClose={handleClose}
            position="bottom"
            height="60vh"
            title={renderMobileTitle()}
            hideCloseIcon
          >
            {renderMobileOptions()}
          </Popup>
        </WapOnly>
      </div>
    );
  }

  // 使用react-hook-form的Controller
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error: fieldError } }) => {
        const open = Boolean(anchorEl);
        const displayText = getDisplayText(field.value || []);

        // 处理选项选择 - 桌面端
        const handleFieldOptionClick = (option: CascaderOption, level: number) => {
          const newPath = [...(field.value || []).slice(0, level), option[valueKey]];

          // 如果有子项，继续展开
          if (option[childrenKey] && option[childrenKey].length > 0) {
            setSelectedPath(newPath);
            field.onChange(newPath); // 同步更新form值
          } else {
            // 叶子节点，完成选择
            setSelectedPath(newPath);
            field.onChange(newPath); // 同步更新form值
            setAnchorEl(null);

            // 触发外部onChange
            if (onChange) {
              onChange(newPath, [option]);
            }
          }
        };

        // 处理选项选择 - 移动端
        const handleFieldMobileOptionClick = (option: CascaderOption, level: number) => {
          const newPath = [...(field.value || []).slice(0, level), option[valueKey]];

          setPendingOption(option);

          // 如果有子项，进入下一层
          if (option[childrenKey] && option[childrenKey].length > 0) {
            setSelectedPath(newPath);
            field.onChange(newPath); // 同步更新form值
            setCurrentLevel(level + 1);
          } else {
            // 叶子节点，完成选择
            setSelectedPath(newPath);
            field.onChange(newPath); // 同步更新form值
            setMobileOpen(false);
            setCurrentLevel(level);

            // 触发外部onChange
            if (onChange) {
              onChange(newPath, [option]);
            }
          }
        };

        // 渲染移动端选项
        const renderFieldMobileOptions = () => {
          const currentOptions = panels[currentLevel] || [];
          return (
            <div>
              {currentOptions.map((option) => {
                const isSelected = selectedPath[currentLevel] === option[valueKey];
                const hasChildren = option[childrenKey] && option[childrenKey].length > 0;

                return (
                  <div
                    key={option[valueKey]}
                    className={`flex items-center justify-between py-9 px-12 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                      isSelected ? 'bg-blue-50' : ''
                    }`}
                    onClick={() => handleFieldMobileOptionClick(option, currentLevel)}
                  >
                    <span
                      className={`text-[14px] ${isSelected ? 'text-primary-purple' : 'text-primary-text'}`}
                    >
                      {option[labelKey]}
                    </span>
                    <div className="flex items-center gap-2">
                      {isSelected && !hasChildren && (
                        <CheckOutlined className="text-[20px] text-primary-purple" />
                      )}
                      {hasChildren && <RightArrowOutlined className="text-[20px] text-gray-400" />}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        };

        // 同步逻辑已移动到组件顶层的useEffect中

        return (
          <div className={`relative ${formItemClassName}`}>
            {layout === 'vertical' && label && (
              <label className="block text-[16px] text-primary-text font-br mb-[12px]">
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            <div className="flex items-center">
              <div className="flex-1 relative">
                <TextField
                  fullWidth={fullWidth}
                  size={size}
                  variant={variant}
                  label={layout === 'vertical' ? undefined : label}
                  placeholder={placeholder}
                  value={displayText}
                  onClick={(e) => {
                    if (isMobile) {
                      setMobileOpen(true);
                    } else {
                      handleInputClick(e);
                    }
                  }}
                  error={!!fieldError}
                  helperText={fieldError?.message}
                  InputProps={{
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        {isMobile ? (
                          <RightArrowOutlined className="text-[16px] text-gray-400" />
                        ) : (
                          <CollapseArrowDownOutlined
                            className={`text-[16px] text-gray-400 transition-transform ${
                              open ? 'rotate-180' : ''
                            }`}
                          />
                        )}
                      </InputAdornment>
                    ),
                  }}
                />

                {/* PC端下拉菜单 */}
                <WebOnly>
                  <Popover
                    open={open}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    className="mt-1"
                  >
                    <div className="max-h-[300px] overflow-y-auto flex justify-center py-8">
                      {panels.map((panel, level) => (
                        <div
                          key={level}
                          className="px-12 inline-block vertical-align-top border-r border-gray-200 last:border-r-0"
                        >
                          <div className="min-w-fit max-w-[300px] mx-auto w-fit">
                            {panel.map((option) => (
                              <div
                                key={option[valueKey]}
                                className={`p-8 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                                  selectedPath[level] === option[valueKey]
                                    ? 'bg-blue-50 text-primary-purple'
                                    : ''
                                }`}
                                onClick={() => handleFieldOptionClick(option, level)}
                              >
                                <div className="flex items-center min-w-fit pr-[30px] relative">
                                  <span className="text-[14px] whitespace-nowrap">
                                    {option[labelKey]}
                                  </span>
                                  {option[childrenKey] && option[childrenKey].length > 0 && (
                                    <RightArrowOutlined className="text-[12px] text-gray-400 absolute right-0" />
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </Popover>
                </WebOnly>
              </div>
            </div>

            {/* 移动端弹窗 */}
            <WapOnly>
              <Popup
                open={mobileOpen}
                onClose={() => {
                  setMobileOpen(false);
                  // setCurrentLevel(0);
                  // 重置panels到初始状态，确保重新打开时能正确显示
                  // setPanels([options]);
                }}
                showDivider={false}
                position="bottom"
                height="60vh"
                title={'Subject'}
              >
                <div className="flex flex-col h-full">
                  {/* 第二部分：已选择部分 */}
                  <div className="px-4 border-b border-gray-200">
                    <Tabs value={currentLevel} variant="scrollable">
                      {selectedPath.map((_value, index) => {
                        const option = panels[index]?.find((opt) => opt[valueKey] === _value);
                        return option ? (
                          <Tab
                            label={option[labelKey]}
                            key={index}
                            onClick={() => {
                              setCurrentLevel(index);
                              // 更新panels到对应级别
                              const newPanels = [];
                              let currentOptions = options;

                              for (let i = 0; i <= index; i++) {
                                if (i === 0) {
                                  newPanels.push(currentOptions);
                                } else {
                                  const parentValue = selectedPath[i - 1];
                                  const parentOption = currentOptions.find(
                                    (opt) => opt[valueKey] === parentValue,
                                  );
                                  if (parentOption && parentOption[childrenKey]) {
                                    currentOptions = parentOption[childrenKey];
                                    newPanels.push(currentOptions);
                                  }
                                }
                              }

                              setPendingOption(option);
                              setPanels(newPanels);
                            }}
                          />
                        ) : null;
                      })}
                      <Tab
                        label="Select"
                        sx={{
                          '&.MuiTab-root': {
                            color: '#808080',
                            display: getOptionHasLeafNode(pendingOption) ? 'flex' : 'none',
                          },
                        }}
                      />
                    </Tabs>
                  </div>

                  {/* 第三部分：列表内容部分 */}
                  <div className="flex-1 overflow-y-auto">{renderFieldMobileOptions()}</div>
                </div>
              </Popup>
            </WapOnly>
          </div>
        );
      }}
    />
  );
};

export default Cascader;
